import classNames from 'classnames';
import { inject } from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import useMaybeExternalLink from '../../../hooks/navigation/useMaybeExternalLink';
import LocationContext from '../../../services/LocationContext';
import RouteService from '../../../services/RouteService';
import LinkType from '../../../types/LinkType';
import Paths from '../../../types/Paths';
import {
  isNotHandledByReactRouter,
  stripHostIfMatches,
} from '../../../util/url';

const MaybeExternalLink = ({
  locationContext,
  routeService,
  to,
  className,
  type,
  accessible,
  active,
  shouldBeVisible,
  isNoFollowLink,
  children,
  ...rest
}) => {
  const location = useLocation();
  const { host } = locationContext;
  const frontPagePath = routeService.getPath(Paths.FrontPage);
  const isFrontPage = location.pathname === frontPagePath;
  const [childPath, getLinkProps] = useMaybeExternalLink({
    path: to,
    type,
    host,
    isFrontPage,
    isNoFollowLink,
  });

  const isExternal = () => {
    return type && type === LinkType.EXTERNAL_LINK;
  };

  const classNameString = classNames('MaybeExternalLink', className, {
    active,
  });

  if (isExternal()) {
    return (
      <a
        href={childPath}
        className={classNameString}
        tabIndex={accessible ? undefined : '-1'}
        {...getLinkProps()}
        {...rest}
        draggable={false}
      >
        {children}
      </a>
    );
  }

  /* Some internal urls might have "external" element containing the domain */
  const url = stripHostIfMatches(childPath, host);
  const isNormalLink = isNotHandledByReactRouter(url);

  return isNormalLink ? (
    <a href={url} className={classNameString} draggable={false} {...rest}>
      {children}
    </a>
  ) : (
    <NavLink
      to={url}
      exact={true}
      tabIndex={accessible ? undefined : '-1'}
      className={classNameString}
      draggable={false}
      isActive={(match) => (typeof active === 'boolean' ? active : match)}
      {...rest}
    >
      {children}
    </NavLink>
  );
};

MaybeExternalLink.propTypes = {
  locationContext: PropTypes.instanceOf(LocationContext).isRequired,
  routeService: PropTypes.instanceOf(RouteService).isRequired,
  to: PropTypes.string.isRequired,
  accessible: PropTypes.bool,
  active: PropTypes.bool,
  className: PropTypes.string,
  overrideTarget: PropTypes.string,
  type: PropTypes.string,
  isNoFollowLink: PropTypes.bool,
};

MaybeExternalLink.defaultProps = {
  accessible: true,
};

export default inject('locationContext', 'routeService')(MaybeExternalLink);
